import React, { useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Phone, Mail, HelpCircle, Clock } from 'lucide-react';

const FAQ = ({ onQuoteClick }) => {
  const faqCategories = [
    {
      category: "Rentals & Pricing",
      questions: [
        {
          q: "How does pricing work?",
          a: "Our pricing is transparent and includes delivery, pickup, and our 12.5% damage waiver. We offer daily, weekly, and monthly rates. The price we quote is the price you pay - no hidden fees."
        },
        {
          q: "What's included in the rental cost?",
          a: "Every rental includes delivery, pickup, damage waiver (12.5%), and 24/7 technical support. For scissor lifts, delivery is $80 each way. For boom lifts, it's $95 each way."
        },
        {
          q: "Do you offer discounts for long-term rentals?",
          a: "Yes! We offer competitive monthly rates that provide significant savings compared to daily or weekly rentals. Contact us for a customized quote."
        },
        {
          q: "Can I extend my rental period?",
          a: "Absolutely. Just give us a call before your rental period ends, and we'll extend it based on availability. You'll be charged the pro-rated daily or weekly rate."
        }
      ]
    },
    {
      category: "Delivery & Service",
      questions: [
        {
          q: "How fast can you deliver?",
          a: "If you call before 12 PM, we offer same-day delivery. Afternoon requests are delivered the next business day. We serve all of California."
        },
        {
          q: "What if the equipment breaks down?",
          a: "We dispatch a technician immediately. If we can't fix it on-site, we'll replace the equipment at no additional charge. That's our commitment to keeping your project on track."
        },
        {
          q: "Do you offer emergency rentals?",
          a: "Yes! Call our 24/7 support line at 1-800-COOPER for emergency equipment needs."
        },
        {
          q: "What's your service area?",
          a: "We serve all of California with multiple locations statewide for faster delivery."
        }
      ]
    },
    {
      category: "Credit & Payment",
      questions: [
        {
          q: "Do you offer credit for contractors?",
          a: "Yes! Contractors with a valid contractor's license can get on-the-spot credit approval. It's one of our most popular features."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept credit cards, checks, ACH transfers, and offer credit terms for qualified contractors."
        },
        {
          q: "When is payment due?",
          a: "Payment is typically due at the end of your rental period. For credit customers, we offer NET 30 terms upon approval."
        }
      ]
    },
    {
      category: "Equipment & Requirements",
      questions: [
        {
          q: "Do I need to be certified to operate the equipment?",
          a: "For most aerial lifts, yes, OSHA requires operator certification. We can recommend training providers, or you can hire one of our certified operators."
        },
        {
          q: "What condition is your equipment in?",
          a: "All our equipment is meticulously maintained and inspected before every rental. We have one of the newest fleets in California."
        },
        {
          q: "Can you help me choose the right equipment?",
          a: "Absolutely! Our team has years of experience and can recommend the perfect equipment for your specific needs. Just call us or request a quote with your project details."
        },
        {
          q: "What's your damage waiver policy?",
          a: "Our damage waiver is just 12.5% - the lowest in the industry. It covers normal wear and tear and most damage (excluding gross negligence)."
        }
      ]
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1a2332] to-gray-800 text-white py-20 -mt-20 pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block p-4 bg-[#E28724]/10 rounded-full mb-6">
            <HelpCircle className="w-12 h-12 text-[#E28724]" />
          </div>
          <h1 className="text-5xl md:text-6xl font-black mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Find answers to common questions about our equipment rentals, pricing, and services.
          </p>
        </div>
      </section>

      {/* Quick Help Stats */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-black text-[#E28724] mb-2">24/7</div>
              <div className="text-gray-600 font-medium">Customer Support</div>
            </div>
            <div>
              <div className="text-4xl font-black text-[#E28724] mb-2">&lt;2hr</div>
              <div className="text-gray-600 font-medium">Average Response Time</div>
            </div>
            <div>
              <div className="text-4xl font-black text-[#E28724] mb-2">100%</div>
              <div className="text-gray-600 font-medium">Satisfaction Guarantee</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <div className="flex items-center mb-6">
                  <div className="w-2 h-8 bg-[#E28724] rounded-full mr-4"></div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    {category.category}
                  </h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                  {category.questions.map((item, itemIndex) => (
                    <AccordionItem 
                      key={itemIndex} 
                      value={`${categoryIndex}-${itemIndex}`}
                      className="bg-gray-50 border border-gray-200 rounded-xl px-6 hover:border-[#E28724] hover:bg-orange-50 transition-all"
                    >
                      <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#E28724] py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-700 leading-relaxed pb-5">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>

          {/* Still Have Questions CTA */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#E28724] to-orange-600 rounded-2xl transform rotate-1"></div>
            <Card className="relative p-10 bg-white border-2 border-[#E28724] rounded-2xl shadow-2xl">
              <div className="text-center">
                <div className="inline-block p-3 bg-orange-100 rounded-full mb-4">
                  <Clock className="w-8 h-8 text-[#E28724]" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Still Have Questions?
                </h3>
                <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
                  Can't find what you're looking for? Our expert team is available 24/7 to help you find the perfect equipment for your project.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                  <Button 
                    onClick={onQuoteClick}
                    size="lg"
                    className="bg-[#E28724] hover:bg-orange-600 text-white px-8 py-6 text-lg font-semibold"
                  >
                    Request a Quote
                  </Button>
                  <Button 
                    variant="outline"
                    size="lg"
                    className="border-2 border-[#E28724] text-[#E28724] hover:bg-[#E28724] hover:text-white px-8 py-6 text-lg font-semibold"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Contact Us
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row gap-6 justify-center text-gray-700">
                  <a href="tel:714-379-5545" className="flex items-center justify-center hover:text-[#E28724] transition-colors font-medium">
                    <Phone className="w-5 h-5 mr-2" />
                    714-379-5545
                  </a>
                  <a href="mailto:info@cooperequipment.com" className="flex items-center justify-center hover:text-[#E28724] transition-colors font-medium">
                    <Mail className="w-5 h-5 mr-2" />
                    info@cooperequipment.com
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;